import { apiRequest, apiUpload } from '@/lib/api-client';

type Service = {
  id: string;
  slug: string;
  title_tr: string;
  title_en: string;
  short_tr: string | null;
  short_en: string | null;
  content_tr: string | null;
  content_en: string | null;
  icon: string | null;
  image_url: string | null;
  published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type NewService = Omit<Service, 'id' | 'created_at' | 'updated_at'>;
type UpdateService = Partial<NewService>;

export async function getAllServices(publishedOnly = false): Promise<Service[]> {
  const endpoint = publishedOnly ? '/services/index.php' : '/services/index.php?all=1';
  return apiRequest<Service[]>(endpoint);
}

export async function getPublishedServices(): Promise<Service[]> {
  return getAllServices(true);
}

export async function getServiceBySlug(slug: string): Promise<Service | null> {
  try {
    return await apiRequest<Service>(`/services/${slug}`);
  } catch (error) {
    return null;
  }
}

export async function getService(slug: string): Promise<Service | null> {
  return getServiceBySlug(slug);
}

export async function getServiceById(id: string): Promise<Service | null> {
  try {
    return await apiRequest<Service>(`/services/${id}`);
  } catch (error) {
    return null;
  }
}

export async function createService(service: NewService): Promise<Service> {
  return apiRequest<Service>('/services/index.php', {
    method: 'POST',
    body: JSON.stringify(service),
  });
}

export async function updateService(id: string, updates: UpdateService): Promise<Service> {
  return apiRequest<Service>(`/services/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteService(id: string): Promise<void> {
  await apiRequest(`/services/${id}`, {
    method: 'DELETE',
  });
}

export async function uploadServiceImage(file: File): Promise<string> {
  return apiUpload(file, 'services/');
}

// Alias functions for compatibility with old naming
export async function getServices(publishedOnly = false): Promise<Service[]> {
  return getAllServices(publishedOnly);
}

export async function addService(service: NewService): Promise<Service> {
  return createService(service);
}

export async function editService({ id, updates }: { id: string; updates: UpdateService }): Promise<Service> {
  return updateService(id, updates);
}

export async function removeService(id: string): Promise<void> {
  return deleteService(id);
}
